<template>
  <section class="news-page">
    <!-- Hero Section -->
    <div class="news-hero">
      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">{{ $t('news.heroTitle') }}</h1>
          <p class="hero-subtitle">{{ $t('news.heroSubtitle') }}</p>
          <div class="hero-decoration">
            <div class="music-note">📰</div>
            <div class="music-note">📢</div>
            <div class="music-note">📝</div>
          </div>
        </div>
      </div>
    </div>

    <!-- News Content Section -->
    <section class="news-section section">
      <div class="container">
        <!-- Search and Filters -->
        <div class="filters-section">
          <div class="search-wrapper">
            <i class="fas fa-search search-icon"></i>
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="$t('news.searchPlaceholder')"
              class="search-input"
            />
            <button v-if="searchQuery" @click="searchQuery = ''" class="clear-button">
              <i class="fas fa-times"></i>
            </button>
          </div>

          <div class="filter-buttons">
            <button
              @click="selectedCategory = null"
              :class="['filter-button', { active: selectedCategory === null }]"
            >
              {{ $t('news.filterAll') }}
            </button>
            <button
              v-for="category in availableCategories"
              :key="category"
              @click="selectedCategory = category"
              :class="['filter-button', { active: selectedCategory === category }]"
            >
              {{ category }}
            </button>
          </div>
        </div>

        <div class="news-content">
          <!-- Loading State -->
          <div v-if="loading" class="loading-grid">
            <NewsCard v-for="i in 6" :key="i" :newsItem="placeholderItem" :loading="true" />
          </div>

          <!-- Empty State -->
          <div v-else-if="filteredNews.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="fas fa-newspaper"></i>
            </div>
            <h3>{{ searchQuery || selectedCategory ? $t('news.noNewsFiltered') : $t('news.noNews') }}</h3>
            <p>{{ searchQuery || selectedCategory ? $t('news.noNewsFilteredDesc') : $t('news.noNewsSoonDesc') }}</p>
            <button v-if="searchQuery || selectedCategory" @click="clearFilters" class="clear-filters-button">
              {{ $t('news.clearFilters') }}
            </button>
          </div>

          <!-- News Grid -->
          <div v-else>
            <!-- Featured News -->
            <div v-if="featuredNews.length > 0" class="featured-section">
              <h2 class="section-subtitle">
                <i class="fas fa-star"></i>
                {{ $t('news.featuredNews') }}
              </h2>
              <div class="featured-grid">
                <NewsCard
                  v-for="item in featuredNews"
                  :key="item.id"
                  :newsItem="item"
                  @click="openNewsDetail"
                />
              </div>
            </div>

            <!-- Regular News -->
            <div v-if="regularPaginatedNews.length > 0" class="regular-section">
              <h2 v-if="featuredNews.length > 0" class="section-subtitle">
                <i class="fas fa-newspaper"></i>
                {{ $t('news.allNews') }}
              </h2>
              <div class="news-grid">
                <NewsCard
                  v-for="item in regularPaginatedNews"
                  :key="item.id"
                  :newsItem="item"
                  @click="openNewsDetail"
                />
              </div>

              <!-- Pagination -->
              <div v-if="totalPages > 1" class="pagination">
                <button
                  @click="currentPage = 1"
                  :disabled="currentPage === 1"
                  class="page-button"
                  aria-label="Primera página"
                >
                  <i class="fas fa-angle-double-left"></i>
                </button>
                <button
                  @click="currentPage--"
                  :disabled="currentPage === 1"
                  class="page-button"
                  aria-label="Página anterior"
                >
                  <i class="fas fa-angle-left"></i>
                </button>

                <div class="page-numbers">
                  <button
                    v-for="page in visiblePages"
                    :key="page"
                    @click="currentPage = page"
                    :class="['page-number', { active: currentPage === page }]"
                  >
                    {{ page }}
                  </button>
                </div>

                <button
                  @click="currentPage++"
                  :disabled="currentPage === totalPages"
                  class="page-button"
                  aria-label="Página siguiente"
                >
                  <i class="fas fa-angle-right"></i>
                </button>
                <button
                  @click="currentPage = totalPages"
                  :disabled="currentPage === totalPages"
                  class="page-button"
                  aria-label="Última página"
                >
                  <i class="fas fa-angle-double-right"></i>
                </button>

                <div class="page-info">
                  {{ $t('news.page') }} {{ currentPage }} {{ $t('news.of') }} {{ totalPages }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- News Detail Modal -->
    <NewsDetailModal
      v-model="showDetailModal"
      v-if="selectedNews"
      :newsItem="selectedNews"
      :shareUrl="getNewsUrl(selectedNews)"
    />
  </section>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
import { useHead } from '@vueuse/head';
import { useNews, type NewsItem } from '@/composables/useNews';
import { absoluteUrl, buildHeadConfig, getBreadcrumbJsonLd } from '../../seo/meta';
import NewsCard from '@/components/news/NewsCard.vue';
import NewsDetailModal from '@/components/news/NewsDetailModal.vue';

const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;

const newsComposable = useNews();
const { fetchNews } = newsComposable;
const news = newsComposable.news;
const loading = newsComposable.loading;

const searchQuery = ref('');
const selectedCategory = ref<string | null>(null);
const currentPage = ref(1);
const itemsPerPage = 9;
const showDetailModal = ref(false);
const selectedNews = ref<NewsItem | null>(null);

// Placeholder for skeleton loading
const placeholderItem: NewsItem = {
  id: 'placeholder',
  title: $t ? $t('news.loading') : 'Cargando...',
  content: $t ? $t('news.loading') : 'Cargando...',
  createdAt: new Date(),
  author: $t ? $t('news.loading') : 'Cargando...',
  featured: false,
  categories: [],
};

// Available categories
const availableCategories = computed(() => {
  const categories = new Set<string>();
  news.value.forEach(item => {
    item.categories?.forEach(cat => categories.add(cat));
  });
  return Array.from(categories).sort();
});

// Filtered news
const filteredNews = computed(() => {
  let filtered = news.value;

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter(item =>
      item.title.toLowerCase().includes(query) ||
      item.content.toLowerCase().includes(query)
    );
  }

  // Filter by category
  if (selectedCategory.value) {
    filtered = filtered.filter(item =>
      item.categories?.includes(selectedCategory.value!)
    );
  }

  return filtered;
});

// Featured news (always shown first)
const featuredNews = computed(() => {
  return filteredNews.value.filter(item => item.featured);
});

// Regular news (not featured)
const regularNews = computed(() => {
  return filteredNews.value.filter(item => !item.featured);
});

// Pagination for regular news
const totalPages = computed(() => {
  return Math.ceil(regularNews.value.length / itemsPerPage);
});

const regularPaginatedNews = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage;
  const end = start + itemsPerPage;
  return regularNews.value.slice(start, end);
});

const visiblePages = computed(() => {
  const pages: number[] = [];
  const maxVisible = 5;
  const total = totalPages.value;
  const current = currentPage.value;

  if (total <= maxVisible) {
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    let start = Math.max(1, current - Math.floor(maxVisible / 2));
    let end = Math.min(total, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
  }

  return pages;
});

// Reset to page 1 when filters change
watch([searchQuery, selectedCategory], () => {
  currentPage.value = 1;
});

const clearFilters = () => {
  searchQuery.value = '';
  selectedCategory.value = null;
};

const openNewsDetail = (newsItem: NewsItem) => {
  selectedNews.value = newsItem;
  showDetailModal.value = true;
};

const getNewsUrl = (newsItem: NewsItem) => {
  return absoluteUrl(`/news#${newsItem.id}`);
};

const newsJsonLd = computed(() =>
  news.value.map((item) => ({
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: item.title,
    datePublished: item.createdAt.toISOString(),
    dateModified: item.createdAt.toISOString(),
    author: {
      '@type': 'Person',
      name: item.author || 'Banda de Música de Noia',
    },
    mainEntityOfPage: absoluteUrl(`/news#${item.id}`),
    url: absoluteUrl('/news'),
    publisher: {
      '@type': 'Organization',
      name: 'Banda de Música de Noia',
      logo: {
        '@type': 'ImageObject',
        url: absoluteUrl('/og-image.jpg'),
      },
    },
  })),
);

useHead(() =>
  buildHeadConfig('news', {
    jsonLd: [...newsJsonLd.value, getBreadcrumbJsonLd('news')],
  }),
);

onMounted(async () => {
  await fetchNews();
});
</script>

<style scoped>
/* News Page */
.news-page {
  background: white;
}

/* Hero Section */
.news-hero {
  position: relative;
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--gradient-music);
  overflow: hidden;
  padding: var(--spacing-20) var(--spacing-4);
}

.news-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
}

.hero-content {
  position: relative;
  text-align: center;
  z-index: 1;
  max-width: 800px;
  margin: 0 auto;
}

.hero-title {
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: var(--font-weight-bold);
  color: white;
  margin: 0 0 var(--spacing-4) 0;
  font-family: var(--font-family-serif);
  text-align: center;
}

.hero-subtitle {
  font-size: var(--font-size-xl);
  color: rgba(255, 255, 255, 0.95);
  margin: 0 0 var(--spacing-6) 0;
  text-align: center;
}

.hero-decoration {
  display: flex;
  gap: var(--spacing-6);
  justify-content: center;
  margin-top: var(--spacing-8);
}

.music-note {
  font-size: 2.5rem;
  color: var(--color-secondary-400);
  animation: float 3s ease-in-out infinite;
  text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.2);
}

.music-note:nth-child(2) {
  animation-delay: 1s;
}

.music-note:nth-child(3) {
  animation-delay: 2s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(10deg);
  }
}

/* News Section */
.news-section {
  background: var(--color-neutral-50);
  padding: var(--spacing-20) 0;
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-4);
}

/* Filters Section */
.filters-section {
  margin-bottom: var(--spacing-10);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-4);
}

.search-wrapper {
  position: relative;
  max-width: 600px;
  margin: 0 auto;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: var(--spacing-4);
  top: 50%;
  transform: translateY(-50%);
  color: var(--color-neutral-400);
  font-size: var(--font-size-lg);
}

.search-input {
  width: 100%;
  padding: var(--spacing-4) var(--spacing-12) var(--spacing-4) var(--spacing-12);
  border: 2px solid var(--color-neutral-300);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-lg);
  transition: var(--transition-all);
  background: white;
  box-shadow: var(--shadow-md);
}

.search-input:focus {
  outline: none;
  border-color: var(--color-primary-500);
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.clear-button {
  position: absolute;
  right: var(--spacing-4);
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--color-neutral-400);
  cursor: pointer;
  padding: var(--spacing-2);
  border-radius: var(--border-radius-full);
  transition: var(--transition-all);
}

.clear-button:hover {
  background: var(--color-neutral-100);
  color: var(--color-neutral-600);
}

.filter-buttons {
  display: flex;
  gap: var(--spacing-2);
  flex-wrap: wrap;
  justify-content: center;
}

.filter-button {
  padding: var(--spacing-2) var(--spacing-4);
  border: 2px solid var(--color-neutral-300);
  background: white;
  border-radius: var(--border-radius-full);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-all);
  font-size: var(--font-size-sm);
}

.filter-button:hover {
  border-color: var(--color-primary-400);
  background: var(--color-primary-50);
  transform: translateY(-2px);
}

.filter-button.active {
  background: var(--color-primary-600);
  color: white;
  border-color: var(--color-primary-600);
}

/* News Content */
.news-content {
  min-height: 400px;
}

.loading-grid,
.news-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: var(--spacing-8);
  margin-bottom: var(--spacing-10);
}

.featured-section {
  margin-bottom: var(--spacing-12);
}

.section-subtitle {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-neutral-900);
  margin: 0 0 var(--spacing-6) 0;
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
}

.section-subtitle i {
  color: var(--color-secondary-500);
}

.featured-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: var(--spacing-8);
}

.regular-section {
  margin-top: var(--spacing-12);
}

.empty-state {
  background: white;
  border-radius: var(--border-radius-xl);
  padding: var(--spacing-12) var(--spacing-6);
  text-align: center;
  box-shadow: var(--shadow-lg);
  margin: var(--spacing-8) auto;
  max-width: 600px;
}

.empty-icon {
  font-size: 4rem;
  color: var(--color-neutral-400);
  margin-bottom: var(--spacing-4);
}

.empty-state h3 {
  font-size: var(--font-size-xl);
  color: var(--color-neutral-600);
  margin: 0 0 var(--spacing-2) 0;
}

.empty-state p {
  color: var(--color-neutral-500);
  margin: 0 0 var(--spacing-4) 0;
}

.clear-filters-button {
  background: var(--color-primary-600);
  color: white;
  border: none;
  padding: var(--spacing-3) var(--spacing-6);
  border-radius: var(--border-radius-lg);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-all);
}

.clear-filters-button:hover {
  background: var(--color-primary-700);
  transform: translateY(-2px);
  box-shadow: var(--shadow-md);
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-2);
  margin-top: var(--spacing-10);
  flex-wrap: wrap;
}

.page-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  background: white;
  border: 2px solid var(--color-neutral-300);
  border-radius: var(--border-radius-lg);
  color: var(--color-neutral-600);
  cursor: pointer;
  transition: var(--transition-all);
  font-size: var(--font-size-base);
}

.page-button:hover:not(:disabled) {
  background: var(--color-primary-50);
  border-color: var(--color-primary-400);
  color: var(--color-primary-600);
  transform: translateY(-2px);
}

.page-button:disabled {
  background: var(--color-neutral-100);
  border-color: var(--color-neutral-200);
  color: var(--color-neutral-300);
  cursor: not-allowed;
  transform: none;
}

.page-numbers {
  display: flex;
  gap: var(--spacing-2);
}

.page-number {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  padding: 0 var(--spacing-2);
  background: white;
  border: 2px solid var(--color-neutral-300);
  border-radius: var(--border-radius-lg);
  color: var(--color-neutral-600);
  cursor: pointer;
  transition: var(--transition-all);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-medium);
}

.page-number:hover {
  background: var(--color-primary-50);
  border-color: var(--color-primary-400);
  color: var(--color-primary-600);
  transform: translateY(-2px);
}

.page-number.active {
  background: var(--color-primary-600);
  border-color: var(--color-primary-600);
  color: white;
  box-shadow: var(--shadow-md);
}

.page-info {
  margin-left: var(--spacing-3);
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .loading-grid,
  .news-grid,
  .featured-grid {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }
}

@media (max-width: 768px) {
  .news-hero {
    min-height: 40vh;
  }

  .hero-subtitle {
    font-size: var(--font-size-lg);
  }

  .music-note {
    font-size: 2rem;
  }

  .news-section {
    padding: var(--spacing-12) 0;
  }

  .loading-grid,
  .news-grid,
  .featured-grid {
    grid-template-columns: 1fr;
    gap: var(--spacing-6);
  }

  .filters-section {
    margin-bottom: var(--spacing-6);
  }

  .search-input {
    font-size: var(--font-size-base);
  }

  .filter-buttons {
    justify-content: flex-start;
  }

  .pagination {
    gap: var(--spacing-1);
  }

  .page-button,
  .page-number {
    width: 36px;
    height: 36px;
    min-width: 36px;
  }

  .page-info {
    width: 100%;
    text-align: center;
    margin: var(--spacing-2) 0 0 0;
    order: -1;
  }
}

/* Refresh 2026 - visual coherence with Home */
.news-page {
  background: transparent;
}

.news-hero {
  min-height: 44vh;
  padding: clamp(2.8rem, 6vw, 5.2rem) var(--spacing-4);
  background:
    radial-gradient(circle at 14% 16%, rgba(219, 174, 80, 0.2), rgba(219, 174, 80, 0) 42%),
    linear-gradient(132deg, #10293b 0%, #1a3c54 52%, #2f6285 100%);
}

.news-hero::before {
  background: radial-gradient(circle at 82% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 45%);
}

.hero-title {
  letter-spacing: var(--letter-spacing-tight);
}

.news-section {
  background: transparent;
}

.search-input,
.filter-button,
.empty-state,
.page-button,
.page-number {
  background: var(--surface-card-strong);
  border-color: rgba(16, 41, 59, 0.16);
}

.search-input:focus {
  box-shadow: 0 0 0 4px rgba(47, 98, 133, 0.16);
}

.filter-button.active,
.page-number.active {
  background: var(--color-primary-700);
  border-color: var(--color-primary-700);
}

.clear-filters-button {
  background: var(--color-primary-700);
}

.clear-filters-button:hover {
  background: var(--color-primary-800);
}
</style>
